import { useState } from 'react';
import { Box, ButtonGroup, TextField, Typography, useTheme } from '@mui/material';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useRef } from 'react';
import { H3 } from 'app/components/Typography';
import { LoadingButton } from '@mui/lab';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { CredentialImage, IMAGE_APIS } from '.';
import { useEffect } from 'react';
import {
  FluxImageForm,
  FreepikForm,
  GPTImageForm,
  IdeogramImageForm,
  MidjourneyForm,
  GeminiImageForm
} from './ImageCredentialForms';

export default function AddCredDialog({ setData, op }) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [values, setValues] = useState({
    name: '',
    channel_id: '',
    server_id: '',
    dc_token: '',
    type: ''
  });
  const formRef = useRef(null);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [credentialType, setCredentialType] = useState('');

  useEffect(() => {
    setValues((p) => ({
      type: credentialType,
      name: '',
      channel_id: '',
      server_id: '',
      dc_token: ''
    }));
  }, [credentialType]);

  const handleClose = async () => {
    if (!formRef.current.reportValidity()) return;

    setIsSaving(true);
    setError(null);

    let fn = httpsCallable(getFunctions(), 'manageUserData');
    const res = await fn({ command: 'addUserCredential', credentials: values });
    setIsSaving(false);
    if (res.data.status === 'error') {
      setError(res.data.message);
    } else {
      setData(values);
    }
  };

  const handleCancel = () => {
    setData(null);
  };

  return (
    <Box>
      <Dialog
        fullScreen={fullScreen}
        open={op}
        onClose={handleCancel}
        aria-labelledby="responsive-dialog-title"
      >
        <DialogTitle id="responsive-dialog-title">Add Credential</DialogTitle>

        <DialogContent>
          <DialogContentText>
            <form ref={formRef}>
              <H3 style={{ minWidth: '500px' }}>Give it a name to recognize</H3>

              <center>
                <TextField
                  fullWidth
                  autoFocus
                  required
                  variant="outlined"
                  placeholder="Name of the Credential"
                  value={values.name}
                  onChange={(e) => setValues({ ...values, name: e.target.value })}
                ></TextField>
              </center>

              <br></br>
              <ButtonGroup fullWidth style={{ marginBottom: '10px' }}>
                <Button
                  variant={credentialType === '' ? 'contained' : 'outlined'}
                  onClick={() => setCredentialType('')}
                >
                  <CredentialImage cred={{}} />
                </Button>
                <Button
                  variant={credentialType === IMAGE_APIS.GPTImage ? 'contained' : 'outlined'}
                  onClick={() => setCredentialType(IMAGE_APIS.GPTImage)}
                >
                  <CredentialImage cred={{ type: IMAGE_APIS.GPTImage }} />
                </Button>
                <Button
                  variant={credentialType === IMAGE_APIS.GeminiImage ? 'contained' : 'outlined'}
                  onClick={() => setCredentialType(IMAGE_APIS.GeminiImage)}
                >
                  <CredentialImage cred={{ type: IMAGE_APIS.GeminiImage }} />
                </Button>
                <Button
                  variant={credentialType === IMAGE_APIS.IdeogramImage ? 'contained' : 'outlined'}
                  onClick={() => setCredentialType(IMAGE_APIS.IdeogramImage)}
                >
                  <CredentialImage cred={{ type: IMAGE_APIS.IdeogramImage }} />
                </Button>

                <Button
                  variant={credentialType === IMAGE_APIS.Freepik ? 'contained' : 'outlined'}
                  onClick={() => setCredentialType(IMAGE_APIS.Freepik)}
                >
                  <CredentialImage cred={{ type: IMAGE_APIS.Freepik }} />
                </Button>

                <Button
                  variant={credentialType === IMAGE_APIS.FLUXImage ? 'contained' : 'outlined'}
                  onClick={() => setCredentialType(IMAGE_APIS.FLUXImage)}
                >
                  <CredentialImage cred={{ type: IMAGE_APIS.FLUXImage }} />
                </Button>
              </ButtonGroup>

              {!values.type && (
                <>
                  <MidjourneyForm values={values} setValues={setValues} />
                  <div style={{ textAlign: 'center', marginTop: '10px' }}>
                    <Typography variant="caption" color="textSecondary">
                      Midjouney setup guide is provided in the credential page.
                    </Typography>
                  </div>
                </>
              )}

              {values.type === IMAGE_APIS.GPTImage && (
                <>
                  <GPTImageForm values={values} setValues={setValues} />

                  <div style={{ textAlign: 'center', marginTop: '10px' }}>
                    <Typography variant="caption" color="textSecondary">
                      Get you GPT Image API Key from{' '}
                      <a
                        href="https://platform.openai.com/api-keys"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ textDecoration: 'underline' }}
                      >
                        here
                      </a>
                      .
                    </Typography>
                  </div>
                </>
              )}
              {values.type === IMAGE_APIS.IdeogramImage && (
                <>
                  <IdeogramImageForm values={values} setValues={setValues} />

                  <div style={{ textAlign: 'center', marginTop: '10px' }}>
                    <Typography variant="caption" color="textSecondary">
                      Get your Ideogram API Key from{' '}
                      <a
                        href="https://ideogram.ai/manage-api"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ textDecoration: 'underline' }}
                      >
                        here
                      </a>
                      .
                    </Typography>
                  </div>
                </>
              )}
              {credentialType === IMAGE_APIS.Freepik && (
                <>
                  <FreepikForm values={values} setValues={setValues} />

                  <div style={{ textAlign: 'center', marginTop: '10px' }}>
                    <Typography variant="caption" color="textSecondary">
                      Get your Freepik API Key from{' '}
                      <a
                        href="https://www.freepik.com/developers/dashboard/api-key"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ textDecoration: 'underline' }}
                      >
                        here
                      </a>
                      .
                    </Typography>
                  </div>
                </>
              )}
              {credentialType === IMAGE_APIS.FLUXImage && (
                <>
                  <FluxImageForm values={values} setValues={setValues} />
                  <div style={{ textAlign: 'center', marginTop: '10px' }}>
                    <Typography variant="caption" color="textSecondary">
                      Get your Flux Image API Key from{' '}
                      <a
                        href="https://dashboard.bfl.ai/api/keys"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ textDecoration: 'underline' }}
                      >
                        here
                      </a>
                      .
                    </Typography>
                  </div>
                </>
              )}

              {credentialType === IMAGE_APIS.GeminiImage && (
                <>
                  <GeminiImageForm values={values} setValues={setValues} />

                  <div style={{ textAlign: 'center', marginTop: '10px' }}>
                    <Typography variant="caption" color="textSecondary">
                      Get your Gemini API Key from{' '}
                      <a
                        href="https://aistudio.google.com/api-keys"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ textDecoration: 'underline' }}
                      >
                        here
                      </a>
                      .
                    </Typography>
                  </div>
                </>
              )}
            </form>

            {error && <p style={{ color: 'red', textAlign: 'center' }}>{error}</p>}
          </DialogContentText>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCancel} variant="outlined">
            Cancel
          </Button>

          <LoadingButton
            loading={isSaving}
            onClick={handleClose}
            color="primary"
            variant="contained"
            autoFocus
          >
            Save
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
